import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Recommendation } from './interfaces/recomendation';
import { useService } from '../../../services/services';
import { DecimalInputComponent } from '../../../components/form/elements/decimal';
import {
  Tab,
  TabContainer,
  TabContents,
  TabLink,
  TabNavigation,
} from '../../../components/common/tabs/tab-container';
import DashboardCard from '../components/dashboard-card';
import DashboardTitle from '../components/dashboard-title';
import DashboardLoading from '../components/dashboard-loading';
import useGeoPosition from '../weather/ml-widget/use-geo-position';
import Details from './details';
import { faClipboardCheck } from '@fortawesome/pro-solid-svg-icons';
import dairyCattleImage from './images/dairy-cattle.svg';
import beefCattleImage from './images/beef-cattle.svg';
import sheapGoatsImage from './images/sheap-goats.svg';
import horsesImage from './images/horses.svg';
import css from './index.module.css';

enum TabsKeys {
  dairy_cattle = 'dairy-cattle',
  beef_cattle = 'beef-cattle',
  sheep_goats = 'sheep-goats',
  horses = 'horses',
}

const TAB_ICONS = {
  [TabsKeys.dairy_cattle]: dairyCattleImage,
  [TabsKeys.beef_cattle]: beefCattleImage,
  [TabsKeys.sheep_goats]: sheapGoatsImage,
  [TabsKeys.horses]: horsesImage,
};

export default function HeatStressIndex() {
  const { t } = useTranslation();
  const transKeyPrefix = 'pages.dashboard.heat_stress_index.recommendations.';
  const tp = useCallback(
    (tab: string, index: string, params = null) => {
      return {
        signs: t(`${transKeyPrefix}${tab}.${index}.signs`, params),
        impact: t(`${transKeyPrefix}${tab}.${index}.impact`, params),
        recommendation: t(
          `${transKeyPrefix}${tab}.${index}.recommendation`,
          params
        ),
      };
    },
    [t]
  );
  const { weatherService } = useService();
  const formMethods = useForm({
    resolver: null,
    mode: 'onChange',
    reValidateMode: 'onChange',
  });
  const [currentTab, setTab] = useState(TabsKeys.dairy_cattle);
  const [heatStressIndex, setHeatStressIndex] = useState<number | null>(null);
  const [recommendations, setRecommendations] = useState<Recommendation | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const t0 = useWatch({ name: 't0', control: formMethods.control });
  const humidity = useWatch({ name: 'humidity', control: formMethods.control });
  const geoPosition = useGeoPosition();

  const tabConfigs = useMemo(
    () => ({
      [TabsKeys.dairy_cattle]: {
        calcIndex: (t0: number, humidity: number) =>
          0.8 * +t0 + +humidity * 0.01 * (+t0 - 14.4) + 46.4,
        ranges: [
          { max: 57, label: '0-57' },
          { max: 65, label: '57-65' },
          { max: 67, label: '65-67' },
          { max: 68, label: '67-68' },
          { max: 69, label: '68-69' },
          { max: 70, label: '69-70' },
          { max: 72, label: '70-72' },
          { max: 79, label: '72-79' },
          { max: 89, label: '79-89' },
          { max: 98, label: '89-98' },
          { max: Infinity, label: '>98' },
        ],
      },
      [TabsKeys.beef_cattle]: {
        calcIndex: (t0: number, humidity: number) =>
          0.8 * +t0 + +humidity * 0.01 * (+t0 - 14.4) + 46.4,
        ranges: [
          { max: 68, label: '<68' },
          { max: 72, label: '68-71' },
          { max: 80, label: '72-79' },
          { max: 90, label: '80-89' },
          { max: Infinity, label: '>90' },
        ],
      },
      [TabsKeys.sheep_goats]: {
        calcIndex: (t0: number, humidity: number) =>
          0.8 * +t0 + +humidity * 0.01 * (+t0 - 14.4) + 46.4,
        ranges: [
          { max: 82, label: '0-82' },
          { max: 84, label: '82-84' },
          { max: 87, label: '84-87' },
          { max: Infinity, label: '>87' },
        ],
      },
      [TabsKeys.horses]: {
        calcIndex: (t0: number, humidity: number) => +t0 + +humidity,
        ranges: [
          { max: 81, label: '0-81' },
          { max: 91, label: '81-91' },
          { max: 101, label: '91-101' },
          { max: 111, label: '101-111' },
          { max: Infinity, label: '>111' },
        ],
      },
    }),
    []
  );

  const calculateHeatStressIndex = useCallback(
    (currentTab: TabsKeys, t0: number, humidity: number) => {
      if (t0 === undefined || humidity === undefined) {
        return;
      }

      let index = 0;
      let recommendations: Recommendation = {
        signs: '',
        impact: '',
        recommendation: '',
        icon: TAB_ICONS[currentTab],
        risk: '', // change to 'high-risk' for red label if needed in conditions
        tabKey: currentTab,
      };

      if (tabConfigs[currentTab]) {
        const { calcIndex, ranges } = tabConfigs[currentTab];
        index = calcIndex(t0, humidity);

        let translationIndex = '';
        for (const range of ranges) {
          if (index < range.max) {
            translationIndex = range.label;
            break;
          }
        }

        recommendations = {
          ...recommendations,
          ...tp(currentTab, translationIndex),
        };
      }

      setHeatStressIndex(index);
      setRecommendations(recommendations);
    },
    [tabConfigs, tp]
  );

  useEffect(() => {
    if (t0 !== undefined && humidity !== undefined) {
      calculateHeatStressIndex(currentTab, t0, humidity);
    }
  }, [t0, humidity, currentTab, calculateHeatStressIndex]);

  useEffect(() => {
    if (!geoPosition) return;

    const subscription = weatherService.getData(geoPosition).subscribe(
      (res) => {
        if (!(res && res.city && res.list && res.list.length)) {
          throw new Error('bad API response structure');
        }

        const dataFor12Hours = res.list.slice(0, 4);
        const averageTemp =
          dataFor12Hours.reduce((acc, item) => acc + item.main.temp, 0) /
          dataFor12Hours.length;
        const averageHumidity =
          dataFor12Hours.reduce((acc, item) => acc + item.main.humidity, 0) /
          dataFor12Hours.length;

        formMethods.setValue('t0', Math.round(averageTemp * 10) / 10);
        formMethods.setValue('humidity', Math.round(averageHumidity));
        setIsLoading(false);
      },
      (error) => {
        console.log('Weather service error:', error);
        setIsLoading(false);
      }
    );

    return () => subscription.unsubscribe();
  }, [geoPosition, formMethods, weatherService]);

  return (
    <DashboardCard>
      <DashboardTitle
        title={t('pages.dashboard.heat_stress_index.heading')}
        icon={faClipboardCheck}
      />

      <FormProvider {...formMethods}>
        <form className={css['config']} data-name="heat-stress-index-block">
          <div className={css['description']}>
            {t('pages.dashboard.heat_stress_index.description')}
          </div>

          <div className={css['controls']}>
            <div className={css['control']}>
              <div className={css['label']}>
                <label htmlFor="t0">
                  {t('pages.dashboard.heat_stress_index.t0')}
                </label>
              </div>
              <DecimalInputComponent name="t0" negative />
            </div>
            <div className={css['control']}>
              <div className={css['label']}>
                <label htmlFor="humidity">
                  {t('pages.dashboard.heat_stress_index.humidity')}
                </label>
              </div>
              <DecimalInputComponent name="humidity" />
            </div>
          </div>
        </form>

        {isLoading ? (
          <DashboardLoading name="dashboard-heat-stress-index-loading" />
        ) : (
          <div className="mx-[-12px]" data-name="heat-stress-index">
            <TabContainer
              selectedTabKey={currentTab}
              setTab={setTab}
              tabNavigation={
                <TabNavigation theme={'pages'}>
                  <TabLink tabKey={TabsKeys.dairy_cattle} theme={'pages'}>
                    {t('pages.dashboard.heat_stress_index.tabs.dairy_cattle')}
                  </TabLink>
                  <TabLink tabKey={TabsKeys.beef_cattle} theme={'pages'}>
                    {t('pages.dashboard.heat_stress_index.tabs.beef_cattle')}
                  </TabLink>
                  <TabLink tabKey={TabsKeys.sheep_goats} theme={'pages'}>
                    {t('pages.dashboard.heat_stress_index.tabs.sheep_goats')}
                  </TabLink>
                  <TabLink tabKey={TabsKeys.horses} theme={'pages'}>
                    {t('pages.dashboard.heat_stress_index.tabs.horses')}
                  </TabLink>
                </TabNavigation>
              }
              tabContents={
                <TabContents theme={'pages'}>
                  <Tab tabKey={TabsKeys.dairy_cattle}>
                    <Details
                      index={heatStressIndex}
                      recommendation={recommendations}
                    />
                  </Tab>
                  <Tab tabKey={TabsKeys.beef_cattle}>
                    <Details
                      index={heatStressIndex}
                      recommendation={recommendations}
                    />
                  </Tab>
                  <Tab tabKey={TabsKeys.sheep_goats}>
                    <Details
                      index={heatStressIndex}
                      recommendation={recommendations}
                    />
                  </Tab>
                  <Tab tabKey={TabsKeys.horses}>
                    <Details
                      index={heatStressIndex}
                      recommendation={recommendations}
                    />
                  </Tab>
                </TabContents>
              }
            />
          </div>
        )}
      </FormProvider>
    </DashboardCard>
  );
}
